import { ShieldCheck, Gift, RefreshCw, PackagePlus, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { useAuth } from "../../context/AuthContext";
import { Plans, errText } from "../../lib/plans";

const STEPS = [
  { n: 1, icon: ShieldCheck, title: "Sign Up & Secure", body: "Sign in with Google and add a card through Stripe. Nothing is charged today.", color: "text-cyan-300 border-cyan-400/40" },
  { n: 2, icon: Gift, title: "Get 10 Free Trial Credits", body: "Process up to 10 images free for 7 days on the plan you picked.", color: "text-purple-300 border-purple-400/40" },
  { n: 3, icon: RefreshCw, title: "Seamless Transition", body: "On day 8, or as soon as your 10 trial credits are used, your plan starts and your monthly credits arrive.", color: "text-emerald-300 border-emerald-400/40" },
];

export function TrialStepper() {
  return (
    <ol className="grid gap-5 md:grid-cols-3" data-testid="trial-stepper">
      {STEPS.map((s, i) => (
        <li key={s.n} style={{ animationDelay: `${i * 100}ms` }} className="suite-rise relative rounded-2xl border border-white/10 bg-slate-950/60 backdrop-blur-xl p-6" data-testid={`trial-step-${s.n}`}>
          <div className={`w-11 h-11 rounded-xl border flex items-center justify-center ${s.color}`}><s.icon size={20} /></div>
          <p className="mt-4 font-mono text-[11px] tracking-[0.25em] text-slate-500">STEP {s.n}</p>
          <p className="mt-1 font-display text-xl font-bold text-white">{s.n}. {s.title}</p>
          <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.body}</p>
        </li>
      ))}
    </ol>
  );
}

export function useBuyAddon() {
  const { user, login } = useAuth();
  const [busy, setBusy] = useState(false);
  const buy = async () => {
    if (!user) return login("/billing?addon=1");
    setBusy(true);
    try {
      window.location.assign((await Plans.addonCheckout()).url);
    } catch (e) {
      toast.error(errText(e, "Could not start checkout"));
      setBusy(false);
    }
  };
  return { buy, busy };
}

export function AddonBanner() {
  const { buy, busy } = useBuyAddon();
  return (
    <div className="rounded-2xl border border-emerald-400/30 bg-gradient-to-r from-emerald-500/10 via-cyan-500/10 to-purple-600/10 p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5 justify-between" data-testid="addon-banner">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-emerald-400/15 border border-emerald-400/40 flex items-center justify-center text-emerald-300"><PackagePlus size={22} /></div>
        <div>
          <p className="font-display text-xl sm:text-2xl font-bold text-white">Need Extra Volume?</p>
          <p className="text-sm text-slate-300">Buy 100 Add-on Credits for $5. They're added to your balance right away and don't expire at month end.</p>
        </div>
      </div>
      <button onClick={buy} disabled={busy} className="shrink-0 px-6 py-3 rounded-full font-semibold text-slate-950 bg-emerald-300 hover:bg-emerald-200 transition-colors" data-testid="addon-buy-btn">
        {busy ? <Loader2 size={16} className="inline animate-spin" /> : "Buy 100 credits · $5"}
      </button>
    </div>
  );
}

const FAQ = [
  ["What is a credit?", "1 Credit = 1 generation or batch-file action. Every image processed in a batch or queue uses exactly 1 credit, and you're shown the total before anything runs."],
  ["Will I be charged during the trial?", "No. Stripe only stores your card. You're charged for the plan you picked on day 8, or earlier if you use all 10 trial credits, whichever comes first."],
  ["Is my card safe?", "Card details go straight to Stripe and never touch our servers. You can update or remove your card in the Stripe customer portal."],
  ["How do I cancel?", "Open Billing and click Cancel Plan. Cancelling during the trial means you're never charged. Paid plans stay active until the end of the month you've paid for."],
  ["What happens to unused credits?", "Monthly plan credits reset at each renewal. Add-on credits stay until you use them."],
  ["Can I change speed?", "Basic queue comes with every plan. Fast Queue starts at Creator, and Priority Queue is part of Pro Suite."],
];

export function FaqSection() {
  return (
    <Accordion type="single" collapsible className="rounded-2xl border border-white/10 bg-slate-950/60 backdrop-blur-xl px-6" data-testid="faq-section">
      {FAQ.map(([q, a], i) => (
        <AccordionItem key={q} value={`q${i}`} className="border-white/10">
          <AccordionTrigger className="text-left text-white hover:no-underline" data-testid={`faq-q-${i}`}>{q}</AccordionTrigger>
          <AccordionContent className="text-slate-400 leading-relaxed">{a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
